import React, { useState, useEffect } from "react";
import { Box, Typography, Pagination, useMediaQuery, useTheme, MenuItem, Select } from "@mui/material";
import AppCardComponent from "../../components/mui/AppCardComponent";
import FinFactorBankCard from "../../components/common/cards/FinFactorBankCard";
import { useGetFinfactorBankAccountsQuery } from "../../store/api/finfactorBank.apislice";
import CircleIcon from "../../assets/circleicon.svg";
import { getBankLogoUrl, getBankInitial } from "../../utils/getLogoUrl";
import { useNavigate, useParams } from "react-router-dom";
import ROUTES from "../../router/routes.constants";
import FinBankComponentContainer from "./FinBankComponentContainer";

const FinFactorBankAccountsContainer = ({ onBack }) => {
    const navigate = useNavigate();
    const { bankId } = useParams();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [bankId]);

    const { data: bankData, isLoading } = useGetFinfactorBankAccountsQuery();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const { bankAccounts, title = "Linked Bank Accounts", totalBalanceLabel = "Total Balance", totalBalance = "₹0", asOfDate = "" } = bankData || {};
    const safeBankAccounts = Array.isArray(bankAccounts) ? bankAccounts : [];
    
    const totalRows = safeBankAccounts.length;
    const displayedAccounts = safeBankAccounts.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    if (isLoading) return null; // Or show skeleton

    if (bankId) {
        const selectedBank = safeBankAccounts.find(b => b?.id?.toString() === bankId);
        return <FinBankComponentContainer bank={selectedBank} onBack={() => navigate(ROUTES.FINFACTOR_BANKING)} />;
    }

    return (
        <AppCardComponent
            title={
                <Box sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: { xs: 0.5, md: 2 },
                    pt: { xs: 0, md: 2 }
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box component="img" src={CircleIcon} sx={{ width: 24, height: 24 }} />
                        <Typography
                            sx={{
                                fontSize: { xs: "20px", md: "24px" },
                                fontWeight: 600,
                                color: "white"
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "13px",
                            pl: { xs: 5, md: 0 },
                            mt: { xs: 0, md: 0.5 }
                        }}
                    >
                        {asOfDate}
                    </Typography>
                </Box>
            }
            action={
                <Box sx={{
                    textAlign: { xs: "left", md: "right" },
                    pl: { xs: 5, md: 0 },
                    pr: 1,
                    mt: { xs: 1.5, md: 2 }
                }}>
                    <Typography
                        variant="caption"
                        color="rgba(255, 255, 255, 0.4)"
                        display="block"
                        sx={{ fontSize: "12px", mb: 0.2 }}
                    >
                        {totalBalanceLabel}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="white" sx={{ fontSize: { xs: "18px", md: "24px" } }}>
                        {totalBalance}
                    </Typography>
                </Box>
            }
            sx={{
                p: { xs: 1.5, md: 3 },
                backgroundColor: "#13172a",
                borderRadius: "12px",
                color: "white",
                "& .MuiCardHeader-root": {
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "flex-start" },
                    p: 0,
                },
                "& .MuiCardHeader-content": {
                    mt: -0.5
                },
                "& .MuiCardContent-root": {
                    p: 0,
                    "&:last-child": { pb: 0 }
                }
            }}
        >
            <Box sx={{ mt: 2 }}>
                {displayedAccounts.map((bank) => (
                    <FinFactorBankCard
                        key={bank.id}
                        bankName={bank.bankName}
                        accountNumber={bank.accountNumber}
                        nomineeStatus={bank.NomineStatus}
                        balance={bank.balance}
                        lastUpdated={bank.lastUpdated}
                        logo={getBankLogoUrl(bank)}
                        fallbackIcon={getBankInitial(bank)}
                        onClick={() => bank?.id && navigate(ROUTES.FINFACTOR_BANK_DETAILS.replace(":bankId", bank.id), { state: { bankName: bank.bankName } })}
                    />
                ))}

                {/* Pagination Footer */}
                {totalRows > rowsPerPage && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 4,
                        mb: 1,
                        gap: 2,
                        px: 1
                    }}>
                        <Typography sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "14px" }}>
                            {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, totalRows)} of {totalRows}
                        </Typography>

                        <Pagination
                            count={Math.ceil(totalRows / rowsPerPage)}
                            page={page}
                            onChange={(e, p) => setPage(p)}
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={isMobile ? 1 : 1}
                            size={isMobile ? "small" : "medium"}
                            showFirstButton={!isMobile}
                            showLastButton={!isMobile}
                            shape="rounded"
                            sx={{
                                "& .MuiPaginationItem-root": {
                                    color: "rgba(255, 255, 255, 0.6)",
                                    backgroundColor: "transparent",
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "6px",
                                    minWidth: { xs: "32px", md: "36px" },
                                    height: { xs: "32px", md: "36px" },
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: "#55B4E3",
                                        color: "white",
                                        border: "none",
                                        "&:hover": {
                                            backgroundColor: "#55B4E3",
                                        }
                                    },
                                    "& .MuiPaginationItem-icon": {
                                        fontSize: { xs: "18px", md: "20px" },
                                        color: "rgba(255, 255, 255, 0.6)"
                                    }
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </AppCardComponent>
    );
};

export default FinFactorBankAccountsContainer;
